import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    Alert,
    CircularProgress,
} from '@mui/material';
import { CheckCircle, XCircle } from 'lucide-react';
import { useVerifyEmail } from '../../../hooks/useAuth';
import { useToastContext } from '../../../context/ToastContext';

export default function VerifyEmail() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [verificationStatus, setVerificationStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();
    const { addToast } = useToastContext();
    const verifyEmailMutation = useVerifyEmail();

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setVerificationStatus('error');
                setErrorMessage('Invalid verification link - no token found.');
                return;
            }

            try {
                await verifyEmailMutation.mutateAsync(token);
                setVerificationStatus('success');
                
                // Clear localStorage to force fresh login with verified status from backend - this fixes state management bug
                localStorage.removeItem('triviaUser');
                
                addToast('Email verified successfully!', 'success');
            } catch (error: any) {
                console.error('Email verification error:', error);
                const message = error.response?.data?.message || 'Verification failed. The link may have expired.';
                setVerificationStatus('error');
                setErrorMessage(message);
                addToast(message, 'error');
            }
        };

        verifyToken();
    }, [token]);

    if (verificationStatus === 'verifying') {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="80vh"
                px={2}
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Card sx={{ maxWidth: 500, width: '100%' }}>
                        <CardContent>
                            <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
                                <CircularProgress size={64} />
                                <Typography variant="h5" align="center">
                                    Verifying Your Email
                                </Typography>
                                <Typography variant="body2" color="text.secondary" align="center">
                                    Please wait while we verify your email address...
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </motion.div>
            </Box>
        );
    }

    if (verificationStatus === 'success') {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="80vh"
                px={2}
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Card sx={{ maxWidth: 500, width: '100%' }}>
                        <CardContent>
                            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                                <CheckCircle size={64} color="#4caf50" />
                                <Typography variant="h5" align="center">
                                    Email Verified!
                                </Typography>
                            </Box>
                            
                            <Alert severity="success" sx={{ mt: 3, mb: 2 }}>
                                Your email has been verified successfully. You can now log in to your account.
                            </Alert>

                            <Button
                                variant="contained"
                                fullWidth
                                onClick={() => navigate('/')}
                            >
                                Go to Login
                            </Button>
                        </CardContent>
                    </Card>
                </motion.div>
            </Box>
        );
    }

    // Error state
    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="80vh"
            px={2}
        >
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Card sx={{ maxWidth: 500, width: '100%' }}>
                    <CardContent>
                        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                            <XCircle size={64} color="#f44336" />
                            <Typography variant="h5" align="center">
                                Verification Failed
                            </Typography>
                        </Box>
                        
                        <Alert severity="error" sx={{ mt: 3, mb: 2 }}>
                            {errorMessage}
                        </Alert>

                        <Typography variant="body2" color="text.secondary" paragraph align="center">
                            The verification link may have expired or is invalid. You can request a new verification email from the login page.
                        </Typography>

                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => navigate('/')}
                        >
                            Go to Login
                        </Button>
                    </CardContent>
                </Card>
            </motion.div>
        </Box>
    );
}